import { Divider } from '@/components/ui/divider'
import { Spinner } from '@/components/ui/spinner'
import EmptyStateIllustration from '@/components/EmptyStateIllustration'
import { getNotificationsThunk, notificationsSelectors, NotificationType } from '@/redux/slices/notificationSlice'
import { useAppDispatch, useAppSelector } from '@/redux/store'
import { FlashList } from '@shopify/flash-list'
import useCustomTabBarHeight from '@/lib/hooks/useCustomTabBarHeight'
import { useCallback, useState } from 'react'
import { ActivityIndicator, View } from 'react-native'
import AdvancedNotificationsEmailFail from './items/AdvancedNotificationsEmailFail'
import AdvancedNotificationsSmsFail from './items/AdvancedNotificationsSmsFail'
import Announcement from './items/Announcement'
import EntriesExport from './items/EntriesExport'
import EntryEmailTaskFail from './items/EntryEmailTaskFail'
import EntrySmsTaskAuditFailure from './items/EntrySmsTaskAuditFailure'
import EntrySmsTaskFail from './items/EntrySmsTaskFail'
import EntrySmsTemplateApprovedWithAutosend from './items/EntrySmsTemplateApprovedWithAutosend'
import EntrySmsTemplateApprovedWithoutAutosend from './items/EntrySmsTemplateApprovedWithoutAutosend'
import FieldVerificationSendSmsFailure from './items/FieldVerificationSendSmsFailure'
import FormBanned from './items/FormBanned'
import FormEntryAttachmentBulkTask from './items/FormEntryAttachmentBulkTask'
import FormEntryPostFailure from './items/FormEntryPostFailure'
import FormSmsSignatureFail from './items/FormSmsSignatureFail'
import JiriRoutineRun from './items/JiriRoutineRun'
import MergedNewEntry from './items/MergedNewEntry'
import PortalSmsSignatureFail from './items/PortalSmsSignatureFail'
import RuleAdvancedNotificationsSmsAuditFailure from './items/RuleAdvancedNotificationsSmsAuditFailure'
import RuleSmsTemplateApprovedWithAutosend from './items/RuleSmsTemplateApprovedWithAutosend'
import RuleSmsTemplateApprovedWithoutAutosend from './items/RuleSmsTemplateApprovedWithoutAutosend'
import SharedRoles from './items/SharedRoles'
import SmsContentAuditRejected from './items/SmsContentAuditRejected'
import SmtpAccountAuthFail from './items/SmtpAccountAuthFail'
import SystemWarning from './items/SystemWarning'
import TradeFreezed from './items/TradeFreezed'
import Unimplemented from './items/Unimplemented'

export default function Notifications() {
  const dispatch = useAppDispatch()
  const customTabBarHeight = useCustomTabBarHeight()

  const notifications = useAppSelector(notificationsSelectors.selectAll)
  const hasNextPage = useAppSelector(state => state.notifications.hasNextPage)
  const currentPage = useAppSelector(state => state.notifications.currentPage)
  const isLoading = useAppSelector(state => state.notifications.isLoading)
  const loaded = useAppSelector(state => state.notifications.loaded)

  const [refreshing, setRefreshing] = useState(false)

  const handleRefresh = useCallback(async () => {
    setRefreshing(true)
    await dispatch(getNotificationsThunk({ page: 1 }))
    setRefreshing(false)
  }, [dispatch])

  const handleLoadMore = useCallback(async () => {
    if (hasNextPage && !isLoading) {
      await dispatch(getNotificationsThunk({ page: currentPage + 1 }))
    }
  }, [hasNextPage, isLoading, currentPage, dispatch])

  const renderFooter = useCallback(() => {
    if (!isLoading) return null
    return (
      <View className="py-4 items-center">
        <ActivityIndicator size="small" />
      </View>
    )
  }, [isLoading])

  if (!loaded && isLoading) {
    return (
      <View className="items-center justify-center flex-1">
        <Spinner />
      </View>
    )
  }

  if (notifications.length === 0) {
    return (
      <View className="items-center justify-center flex-1">
        <EmptyStateIllustration message="暂无消息" />
      </View>
    )
  }

  return (
    <FlashList
      data={notifications}
      keyExtractor={item => item.id}
      renderItem={({ item }) => <NotificationItem item={item} />}
      contentInsetAdjustmentBehavior="automatic"
      contentContainerStyle={{ paddingBottom: customTabBarHeight }}
      maintainVisibleContentPosition={{ disabled: true }}
      refreshing={refreshing}
      onRefresh={handleRefresh}
      onEndReached={handleLoadMore}
      onEndReachedThreshold={0.1}
      ListFooterComponent={renderFooter}
    />
  )
}

function NotificationItem({ item }: { item: NotificationType }) {
  const key = item.verb as keyof typeof NotificationComponentMap
  const Component = NotificationComponentMap[key]?.component ?? Unimplemented

  return (
    <>
      <Component notification={item} />
      <Divider />
    </>
  )
}

const NotificationComponentMap = {
  merged_new_entry: { component: MergedNewEntry },
  jiri_routine_failed: { component: JiriRoutineRun },
  jiri_routine_paused: { component: JiriRoutineRun },
  entries_export: { component: EntriesExport },
  share_roles: { component: SharedRoles },
  advanced_notifications_sms_audit_failure: { component: RuleAdvancedNotificationsSmsAuditFailure },
  entry_sms_task_audit_failure: { component: EntrySmsTaskAuditFailure },
  portal_sms_signature_fail: { component: PortalSmsSignatureFail },
  field_verification_send_sms_fail: { component: FieldVerificationSendSmsFailure },
  form_sms_signature_fail: { component: FormSmsSignatureFail },
  entry_attachment_bulk_task: { component: FormEntryAttachmentBulkTask },
  smtp_account_auth_fail: { component: SmtpAccountAuthFail },
  entry_post_failure: { component: FormEntryPostFailure },
  form_banned: { component: FormBanned },
  sms_content_audit_rejected: { component: SmsContentAuditRejected },
  entry_sms_template_approved_with_autosend: { component: EntrySmsTemplateApprovedWithAutosend },
  entry_sms_template_approved_without_autosend: { component: EntrySmsTemplateApprovedWithoutAutosend },
  rule_sms_template_approved_with_autosend: { component: RuleSmsTemplateApprovedWithAutosend },
  rule_sms_template_approved_without_autosend: { component: RuleSmsTemplateApprovedWithoutAutosend },
  entry_sms_task_fail: { component: EntrySmsTaskFail },
  entry_email_task_fail: { component: EntryEmailTaskFail },
  advanced_notifications_sms_fail: { component: AdvancedNotificationsSmsFail },
  advanced_notifications_email_fail: { component: AdvancedNotificationsEmailFail },
  announcement: { component: Announcement },
  system_warning: { component: SystemWarning },
  trade_freezed: { component: TradeFreezed },
}
